import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Alert, IconButton, InputAdornment, Link, Stack } from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { useAuthContext } from "../../auth/useAuthContext";
import { useLocales } from "../../locales";
import { PATH_AUTH } from "../../routes/paths";
import FormProvider, { RHFTextField } from "../../ui/hook-form";
import Iconify from "../../ui/iconify";

type FormValuesProps = {
  email: string;
  password: string;
  afterSubmit?: string;
};

export default function AuthLoginForm() {
  const { login } = useAuthContext();
  const { translate } = useLocales();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required(translate("validation.email_required") as unknown as string)
      .email(translate("validation.email_invalid") as unknown as string),
    password: Yup.string().required(translate("validation.password_required") as unknown as string),
  });

  const defaultValues = {
    email: "",
    password: "",
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const navigate = useNavigate();

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const loginData = await login(data.email, data.password);
      if (typeof loginData?.email === "string") {
        sessionStorage.setItem("email-recovery", loginData.email);
        navigate(PATH_AUTH.newPassword);
      }
    } catch (error) {
      reset();
      if (axios.isAxiosError(error)) {
        setError("afterSubmit", {
          ...error,
          message: error.response?.data.message,
        });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}

        <RHFTextField name="email" label={translate("forms.email_address") as unknown as string} />

        <RHFTextField
          name="password"
          label={translate("forms.password") as unknown as string}
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack alignItems="flex-end" sx={{ my: 2 }}>
        <Link
          component={RouterLink}
          to={PATH_AUTH.resetPassword}
          variant="body2"
          color="inherit"
          underline="always"
        >
          {translate("buttons.forgot_password") as unknown as string}
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting}
      >
        {translate("buttons.login") as unknown as string}
      </LoadingButton>
    </FormProvider>
  );
}
